/**
 * WeatherCard Component
 *
 * Visar väderinformation för en punkt längs rutten.
 * Inkluderar temperatur, väderikon, vind och ev. varningar.
 */

import { Wind, Droplets, Clock, MapPin } from 'lucide-react';
import { getWeatherEmoji } from '@/services/smhi';
import type { RouteWeatherPoint } from '@/types';

interface WeatherCardProps {
  weatherPoint: RouteWeatherPoint;
  isFirst?: boolean;
  isLast?: boolean;
  index?: number;
}

/**
 * Formatera avstånd i km
 */
function formatDistance(meters: number): string {
  const km = meters / 1000;
  if (km < 1) {
    return `${Math.round(meters)} m`;
  }
  return `${Math.round(km)} km`;
}

/**
 * Formatera tid (HH:mm)
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formatera vindhastighet
 */
function formatWind(speedMs: number): string {
  if (speedMs < 3) return 'Svag vind';
  if (speedMs < 8) return 'Måttlig vind';
  if (speedMs < 14) return 'Frisk vind';
  if (speedMs < 21) return 'Hård vind';
  return 'Storm';
}

/**
 * Få bakgrundsfärg baserat på temperatur
 */
function getTemperatureColor(temp: number): string {
  if (temp < -10) return 'from-blue-600 to-blue-400';
  if (temp < 0) return 'from-blue-500 to-cyan-400';
  if (temp < 10) return 'from-cyan-500 to-teal-400';
  if (temp < 20) return 'from-teal-500 to-green-400';
  if (temp < 25) return 'from-yellow-500 to-orange-400';
  return 'from-orange-500 to-red-400';
}

export function WeatherCard({
  weatherPoint,
  isFirst = false,
  isLast = false,
  index = 0,
}: WeatherCardProps) {
  const { weather, distanceFromStart, estimatedArrival, warnings, location } = weatherPoint;
  const hasWarning = warnings.length > 0;
  const highestWarning = warnings[0];

  const warningBorderColor = hasWarning
    ? highestWarning?.level === 'RED'
      ? 'ring-2 ring-red-500'
      : highestWarning?.level === 'ORANGE'
      ? 'ring-2 ring-orange-500'
      : highestWarning?.level === 'YELLOW'
      ? 'ring-2 ring-yellow-500'
      : ''
    : '';

  return (
    <div
      className={`card relative overflow-hidden ${warningBorderColor}`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Gradient bakgrund baserat på temperatur */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${getTemperatureColor(weather.temperature)} opacity-10`}
      />

      {/* Innehåll */}
      <div className="relative">
        {/* Header med platsinfo */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isFirst ? (
              <span className="text-lg">🚗</span>
            ) : isLast ? (
              <span className="text-lg">🏁</span>
            ) : (
              <MapPin className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-sm font-medium text-gray-600">
              {isFirst
                ? 'Start'
                : isLast
                ? 'Destination'
                : formatDistance(distanceFromStart * 1000)}
            </span>
          </div>

          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>{formatTime(estimatedArrival)}</span>
          </div>
        </div>

        {/* Huvudväderinfo */}
        <div className="flex items-center gap-4">
          {/* Väderikon */}
          <div className="text-5xl">
            {getWeatherEmoji(weather.weatherSymbol, location, estimatedArrival)}
          </div>

          {/* Temperatur */}
          <div className="flex-1">
            <div className="text-3xl font-bold text-gray-900">
              {Math.round(weather.temperature)}°
            </div>
            <div className="text-sm text-gray-500">
              Känns som {Math.round(weather.temperature - weather.windSpeed * 0.5)}°
            </div>
          </div>
        </div>

        {/* Detaljerad info */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {/* Vind */}
          <div className="flex items-center gap-2 text-sm">
            <Wind className="w-4 h-4 text-blue-500" />
            <div>
              <div className="text-gray-900 font-medium">
                {Math.round(weather.windSpeed)} m/s
              </div>
              <div className="text-xs text-gray-500">
                {formatWind(weather.windSpeed)}
              </div>
            </div>
          </div>

          {/* Nederbörd */}
          <div className="flex items-center gap-2 text-sm">
            <Droplets className="w-4 h-4 text-cyan-500" />
            <div>
              <div className="text-gray-900 font-medium">
                {weather.precipitation.toFixed(1)} mm
              </div>
              <div className="text-xs text-gray-500">
                {weather.precipitation === 0
                  ? 'Uppehåll'
                  : weather.precipitation < 1
                  ? 'Lätt nederbörd'
                  : 'Nederbörd'}
              </div>
            </div>
          </div>
        </div>

        {/* Varnings-badge */}
        {hasWarning && highestWarning && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2
              ${
                highestWarning.level === 'RED'
                  ? 'bg-red-100 text-red-800'
                  : highestWarning.level === 'ORANGE'
                  ? 'bg-orange-100 text-orange-800'
                  : highestWarning.level === 'YELLOW'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
          >
            <span>⚠️</span>
            <span className="truncate">
              {highestWarning.description.sv}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
